import { useState } from "react";
import { IconBan } from "@tabler/icons-react";
import { EditRowActions, Input, Stack, Td, Text, Tr, useConfirmDialog } from "@/components/ui";
import type { ProveedorAdmin } from "@/lib/types";
import { useDarDeBajaProveedor, useEditarProveedor, useReactivarProveedor } from "../hooks/use-proveedores-mutations";
import { EstadoBadge } from "./ProveedorBadge";

/**
 * Fila de proveedor con edit inline · razón social, banco, cuenta y
 * CLABE editables; número y RFC inmutables (cambian la identidad).
 * Mutations encapsuladas · el padre solo provee `prov`.
 */
export function ProveedorRow({ prov }: { prov: ProveedorAdmin }) {
  const [editando, setEditando] = useState(false);
  const [razonSocial, setRazonSocial] = useState(prov.razonSocial);
  const [banco, setBanco] = useState(prov.datosBancarios.banco);
  const [cuenta, setCuenta] = useState(prov.datosBancarios.cuenta);
  const [clabe, setClabe] = useState(prov.datosBancarios.clabe ?? "");
  const confirm = useConfirmDialog();

  const guardar = useEditarProveedor({ onEditado: () => setEditando(false) });
  const baja = useDarDeBajaProveedor();
  const reactivar = useReactivarProveedor();

  const cancelar = () => {
    setRazonSocial(prov.razonSocial);
    setBanco(prov.datosBancarios.banco);
    setCuenta(prov.datosBancarios.cuenta);
    setClabe(prov.datosBancarios.clabe ?? "");
    setEditando(false);
  };

  const confirmarBaja = async () => {
    const ok = await confirm({
      title: `Dar de baja proveedor #${prov.numeroProveedor}`,
      message: (
        <Stack gap="xs">
          <Text size="sm">
            <b>{prov.razonSocial}</b> quedará INACTIVO y no podrá asignarse a nuevas solicitudes.
          </Text>
          <Text size="sm" c="dimmed">
            Las solicitudes existentes con este proveedor se preservan. Puedes reactivarlo
            después si lo necesitas.
          </Text>
        </Stack>
      ),
      confirmLabel: "Dar de baja",
      confirmColor: "peligro",
      icon: <IconBan size={14} />,
    });
    if (ok) baja.mutate({ numero: prov.numeroProveedor });
  };

  if (editando) {
    return (
      <Tr className="bg-muted/50">
        <Td className="font-mono">{prov.numeroProveedor}</Td>
        <Td>
          <Text size="xs" ff="mono">
            {prov.rfc}
          </Text>
        </Td>
        <Td>
          <Input size="xs" value={razonSocial} onChange={(e) => setRazonSocial(e.currentTarget.value)} autoFocus />
        </Td>
        <Td>
          <Input size="xs" value={banco} onChange={(e) => setBanco(e.currentTarget.value)} />
        </Td>
        <Td>
          <Input size="xs" className="font-mono" value={cuenta} onChange={(e) => setCuenta(e.currentTarget.value)} />
        </Td>
        <Td>
          <EstadoBadge activo={prov.estaActivo} />
        </Td>
        <Td align="right">
          <EditRowActions
            mode="edit"
            saving={guardar.isPending}
            onCancel={cancelar}
            onSave={() =>
              guardar.mutate({
                numero: prov.numeroProveedor,
                input: { razonSocial: razonSocial.trim(), banco: banco.trim(), cuenta: cuenta.trim(), clabe: clabe.trim() || null },
              })
            }
          />
        </Td>
      </Tr>
    );
  }

  return (
    <Tr>
      <Td className="font-mono">{prov.numeroProveedor}</Td>
      <Td>
        <Text size="xs" ff="mono">
          {prov.rfc}
        </Text>
      </Td>
      <Td>
        <Text size="sm">{prov.razonSocial}</Text>
      </Td>
      <Td>
        <Text size="sm">{prov.datosBancarios.banco}</Text>
      </Td>
      <Td>
        <Text size="xs" ff="mono">
          {prov.datosBancarios.cuenta}
        </Text>
      </Td>
      <Td>
        <EstadoBadge activo={prov.estaActivo} />
      </Td>
      <Td align="right">
        <EditRowActions
          mode="view"
          onEdit={() => setEditando(true)}
          onDelete={prov.estaActivo ? confirmarBaja : undefined}
          deleteVariant="baja"
          deleting={baja.isPending}
          reactivable={!prov.estaActivo}
          onReactivar={() => reactivar.mutate({ numero: prov.numeroProveedor })}
          reactivando={reactivar.isPending}
        />
      </Td>
    </Tr>
  );
}
